import React, { useState, useEffect } from 'react';
import { FiGlobe, FiPlus, FiTrash2, FiRefreshCw, FiAlertCircle, FiCopy, FiCheck, FiX, FiShield, FiClock } from 'react-icons/fi';
import Calling from '../../api/Calling';
import formatDate from '../../components/formatDate';
import { toast } from "react-toastify";
import generateCaptcha from '../../components/captcha';

const IpManager = () => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);

    // Add Modal State
    const [addModal, setAddModal] = useState(false);
    const [newIp, setNewIp] = useState('');
    const [addLoading, setAddLoading] = useState(false);


    const [deleteRecord, setDeleteRecord] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Fetch IP whitelist on mount
    useEffect(() => {
        fetchIpWhitelist();
    }, []);

    // Fetch IP Whitelist
    const fetchIpWhitelist = async () => {
        setLoading(true);
        setError(null);
        setSelectedIds([]);
        try {
            const resp = await Calling.fetchIpWhitelist();
            if (resp?.status) {
                setRecords(resp.data || []);
            } else {
                setError(resp?.message || 'Failed to fetch IP whitelist');
                setRecords([]);
            }
        } catch (err) {
            setError('Something went wrong');
            setRecords([]);
        }
        setLoading(false);
    };

    // Add IP to Whitelist
    const handleAddIp = async () => {
        const ip = newIp.trim();

        // Basic IP validation
        const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        if (!ip) {
            toast.warning("Please enter an IP address", { autoClose: 2000 });
            return;
        }
        if (!ipRegex.test(ip)) {
            toast.error("Please enter a valid IP address", { autoClose: 2000 });
            return;
        }

        // Check for duplicate
        if (records.some(r => r.ipAddress === ip)) {
            toast.warning("This IP is already whitelisted", { autoClose: 2000 });
            return;
        }

        setAddLoading(true);
        try {
            const resp = await Calling.addIpToWhitelist({ ipAddress: ip });
            if (resp?.status) {
                toast.success("IP added successfully", { autoClose: 2000 });
                setAddModal(false);
                setNewIp('');
                fetchIpWhitelist();
            } else {
                toast.error(resp?.message || "Failed to add IP", { autoClose: 2000 });
            }
        } catch (err) {
            toast.error("Something went wrong", { autoClose: 2000 });
        }
        setAddLoading(false);
    };

    // Delete IP from Whitelist (Single)
    const handleDelete = async (record) => {
        if (!record?._id) return;
        const check = generateCaptcha("to confirm deletion of the IP address");
        if (!check) {
            toast.error("Captcha verification failed", { autoClose: 2000 });
            return;
        }
        setDeleteLoading(true);
        setDeleteRecord(record);
        try {
            const resp = await Calling.removeIpFromWhitelist({ ids: [record._id] });
            if (resp?.status) {
                toast.success("IP removed successfully", { autoClose: 2000 });
                setDeleteRecord(null);
                setRecords(prev => prev.filter(r => r._id !== record._id));
                setSelectedIds(prev => prev.filter(id => id !== record._id));
            } else {
                toast.error(resp?.message || "Failed to remove IP", { autoClose: 2000 });
            }
        } catch (err) {
            toast.error("Something went wrong", { autoClose: 2000 });
        }
        setDeleteLoading(false);
        setDeleteRecord(null);
    };

    // Bulk Delete
    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        const check = generateCaptcha(`to confirm deletion of ${selectedIds.length} IP address(es)`);
        if (!check) {
            toast.error("Captcha verification failed", { autoClose: 2000 });
            return;
        }
        setDeleteLoading(true);
        try {
            const resp = await Calling.removeIpFromWhitelist({ ids: selectedIds });
            if (resp?.status) {
                toast.success(`${selectedIds.length} IP(s) removed successfully`, { autoClose: 2000 });
                setRecords(prev => prev.filter(r => !selectedIds.includes(r._id)));
                setSelectedIds([]);
            } else {
                toast.error(resp?.message || "Failed to remove IPs", { autoClose: 2000 });
            }
        } catch (err) {
            toast.error("Something went wrong", { autoClose: 2000 });
        }
        setDeleteLoading(false);
    };

    // Toggle select single
    const toggleSelect = (id) => {
        setSelectedIds(prev => 
            prev.includes(id) 
                ? prev.filter(i => i !== id) 
                : [...prev, id]
        );
    };

    // Toggle select all
    const toggleSelectAll = () => {
        if (selectedIds.length === records.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(records.map(r => r._id));
        }
    };

    // Copy to clipboard
    const handleCopy = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        toast.success("Copied!", { autoClose: 1000 });
        setTimeout(() => setCopiedId(null), 2000);
    };

    // Handle Enter key in add modal
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAddIp();
        }
    };

    return (
        <div className="w-full min-h-screen bg-main-bg">
            <div className="p-4 md:p-6 md:ml-[4.4rem] pt-20 md:pt-24">

                {/* Header Card */}
                <div className="bg-main-white rounded-2xl border border-main-border p-5 md:p-6 mb-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        {/* Title */}
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-main-th rounded-2xl flex items-center justify-center">
                                <FiShield className="text-main-blue text-2xl" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-main-font">IP Whitelist</h1>
                                <p className="text-sm text-main-subfont mt-0.5">Manage allowed IP addresses</p>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3">
                            <button
                                onClick={fetchIpWhitelist}
                                disabled={loading}
                                className="flex items-center justify-center gap-2 px-4 py-3 bg-main-bg text-main-font rounded-xl hover:bg-main-th transition-all font-medium text-sm border border-main-border disabled:opacity-50"
                            >
                                <FiRefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                                Refresh
                            </button>
                            <button
                                onClick={() => setAddModal(true)}
                                className="flex items-center justify-center gap-2 px-6 py-3 bg-main-blue text-main-white rounded-xl hover:bg-emerald-600 transition-all font-semibold text-sm shadow-lg shadow-main-blue/20"
                            >
                                <FiPlus className="w-5 h-5" />
                                Add IP
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="bg-main-white rounded-2xl border border-main-border">

                    {/* Stats Bar */}
                    <div className="p-4 border-b border-main-border bg-main-bg/50">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-main-subfont">Total IPs:</span>
                                    <span className="px-3 py-1.5 bg-main-blue/10 text-main-blue text-sm font-medium rounded-lg">
                                        {records.length}
                                    </span>
                                </div>
                                {selectedIds.length > 0 && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-main-subfont">Selected:</span>
                                        <span className="px-3 py-1.5 bg-red-100 text-main-delete text-sm font-medium rounded-lg">
                                            {selectedIds.length}
                                        </span>
                                    </div>
                                )}
                            </div>
                            {selectedIds.length > 0 && (
                                <button
                                    onClick={handleBulkDelete}
                                    disabled={deleteLoading}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-main-delete text-white rounded-xl font-medium text-sm hover:bg-red-600 transition-all disabled:opacity-50"
                                >
                                    {deleteLoading ? (
                                        <FiRefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <FiTrash2 className="w-4 h-4" />
                                    )}
                                    Delete ({selectedIds.length})
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="p-5 md:p-6">

                        {/* Error */}
                        {error && (
                            <div className="flex items-center gap-3 p-4 mb-4 rounded-xl bg-red-50 border border-red-200">
                                <FiAlertCircle className="text-main-delete text-xl" />
                                <p className="text-main-delete text-sm">{error}</p>
                            </div>
                        )}

                        {/* Loading */}
                        {loading && (
                            <div className="space-y-3">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="animate-pulse bg-main-bg rounded-xl p-4 border border-main-border">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-main-border rounded-full" />
                                            <div className="flex-1 space-y-2">
                                                <div className="h-4 bg-main-border rounded w-1/3" />
                                                <div className="h-3 bg-main-border rounded w-1/4" />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Empty State */}
                        {!loading && !error && records.length === 0 && (
                            <div className="text-center py-20">
                                <div className="w-28 h-28 bg-gradient-to-br from-main-th to-main-thHover rounded-full flex items-center justify-center mx-auto mb-6">
                                    <FiGlobe className="text-main-blue text-5xl" />
                                </div>
                                <h2 className="text-2xl font-bold text-main-font">No IPs Whitelisted</h2>
                                <p className="text-main-subfont mt-2 max-w-md mx-auto">
                                    Add IP addresses to allow access
                                </p>
                                <button
                                    onClick={() => setAddModal(true)}
                                    className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-main-blue text-main-white rounded-xl hover:bg-emerald-600 transition-all font-semibold text-sm"
                                >
                                    <FiPlus className="w-5 h-5" />
                                    Add First IP
                                </button>
                            </div>
                        )}

                        {/* IP List */}
                        {!loading && records.length > 0 && (
                            <div className="space-y-3">
                                {/* Select All */}
                                <div className="flex items-center gap-3 px-4 py-2">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.length === records.length && records.length > 0}
                                        onChange={toggleSelectAll}
                                        className="w-5 h-5 rounded border-main-border text-main-blue focus:ring-main-blue/20 cursor-pointer"
                                    />
                                    <span className="text-sm text-main-subfont font-medium">
                                        Select All
                                    </span>
                                </div>

                                {records.map((record) => (
                                    <div
                                        key={record._id}
                                        className={`rounded-xl p-4 border bg-main-bg transition-all ${
                                            selectedIds.includes(record._id) 
                                                ? 'border-main-blue/50 bg-main-blue/5' 
                                                : 'border-main-border hover:border-main-blue/30'
                                        }`}
                                    >
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.includes(record._id)}
                                                    onChange={() => toggleSelect(record._id)}
                                                    className="w-5 h-5 rounded border-main-border text-main-blue focus:ring-main-blue/20 cursor-pointer"
                                                />
                                                <div className="w-12 h-12 bg-main-th rounded-full flex items-center justify-center flex-shrink-0">
                                                    <FiGlobe className="text-main-blue text-xl" />
                                                </div>
                                                <div>
                                                    <button
                                                        onClick={() => handleCopy(record.ipAddress, record._id)}
                                                        className="flex items-center gap-2 text-lg font-mono font-semibold text-main-font hover:text-main-blue transition-all"
                                                    >
                                                        {record.ipAddress}
                                                        {copiedId === record._id ? (
                                                            <FiCheck className="w-4 h-4 text-main-blue" />
                                                        ) : (
                                                            <FiCopy className="w-4 h-4 text-main-subfont" />
                                                        )}
                                                    </button>
                                                    <p className="text-xs text-main-subfont flex items-center gap-1 mt-1">
                                                        <FiClock className="w-3 h-3" />
                                                        Added {formatDate(record.createdAt)}
                                                    </p>
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => handleDelete(record)}
                                                disabled={deleteLoading && deleteRecord?._id === record._id}
                                                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm text-main-delete hover:bg-red-50 rounded-xl font-medium transition-all border border-transparent hover:border-red-200 disabled:opacity-50"
                                            >
                                                {deleteLoading && deleteRecord?._id === record._id ? (
                                                    <FiRefreshCw className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <FiTrash2 className="w-4 h-4" />
                                                )}
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Add IP Modal */}
            {addModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => !addLoading && setAddModal(false)}>
                    <div className="bg-main-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <div className="p-5 border-b border-main-border flex items-center justify-between">
                            <h3 className="font-bold text-lg text-main-font">Add IP Address</h3>
                            <button
                                onClick={() => setAddModal(false)}
                                disabled={addLoading}
                                className="w-9 h-9 rounded-xl bg-main-bg hover:bg-main-th flex items-center justify-center text-main-subfont hover:text-main-font transition-all disabled:opacity-50"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-5">
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-main-font mb-2">
                                    IP Address
                                </label>
                                <div className="relative">
                                    <FiGlobe className="absolute left-4 top-1/2 -translate-y-1/2 text-main-subfont w-5 h-5" />
                                    <input
                                        type="text"
                                        value={newIp}
                                        onChange={(e) => setNewIp(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="e.g. 192.168.1.1"
                                        className="w-full pl-12 pr-4 py-3.5 border border-main-border rounded-xl focus:outline-none focus:border-main-blue focus:ring-2 focus:ring-main-blue/20 text-sm bg-main-white text-main-font placeholder-main-subfont transition-all font-mono"
                                        autoFocus
                                    />
                                </div>
                                <p className="text-xs text-main-subfont mt-2">
                                    Enter a valid IPv4 address to whitelist
                                </p>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setAddModal(false)}
                                    disabled={addLoading}
                                    className="flex-1 px-4 py-3 bg-main-bg text-main-font rounded-xl font-medium hover:bg-main-th transition-all border border-main-border disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddIp}
                                    disabled={addLoading || !newIp.trim()}
                                    className="flex-1 px-4 py-3 bg-main-blue text-white rounded-xl font-medium hover:bg-emerald-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {addLoading ? (
                                        <>
                                            <FiRefreshCw className="w-5 h-5 animate-spin" />
                                            Adding...
                                        </>
                                    ) : (
                                        <>
                                            <FiPlus className="w-5 h-5" />
                                            Add IP
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        
        </div>
    );
};

export default IpManager;